import { FeatureFilters, FeatureType, useFilters } from '@/composables/useFilters'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import isEqual from 'lodash/isEqual'
import { useFetchGet } from './useFetch'

export const useFetchFeature = <Feature extends FeatureType, Query, Response, Data>(
  type: Feature,
  name: string,
  path: string,
  createQuery: (filters: FeatureFilters<Feature>) => Query | undefined,
  transform: (response: Response) => Data | undefined
) => {
  const store = defineStore(name + type, () => {
    const filters = useFilters(type)
    const query = computed<Query | undefined>(() =>
      filters.submitted ? createQuery(filters.submitted as FeatureFilters<Feature>) : undefined
    )

    const prevQuery = ref<Query | undefined>()
    const urlWithParams = computed(() => {
      if (!query.value || isEqual(query, prevQuery.value)) return undefined

      prevQuery.value = query.value
      const urlQuery = encodeURIComponent(JSON.stringify(query.value))
      return `${path}?query=${urlQuery}`
    })

    const { data, isLoading, error, cancel, fetch } = useFetchGet<Response>(urlWithParams)

    return {
      data,
      isLoading,
      error,
      cancel,
      fetch
    }
  })()

  const { cancel, fetch } = store
  const { data, isLoading, error } = storeToRefs(store)

  return {
    data: computed(() => (data.value ? transform(data.value) : undefined)),
    isLoading,
    error,
    cancel,
    fetch
  }
}
